import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Link2, Search } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/Feedback';
import { listAuditEvents } from '@/lib/api/endpoints/audit';
import { queryKeys } from '@/lib/query-keys';
import { formatAbsolute, formatNumber, formatRelative, humaniseAction } from '@/lib/format';
import type { AuditOutcome, AuditSeverity } from '@/lib/api/types';

/*
  Audit log viewer.

  Outcome is a first class column, not folded into severity. A denied action with severity
  "notice" is the interesting row in an investigation: docs/07-identity-rbac.md I.3 records
  authorization denials deliberately, so that a technician probing devices they cannot reach
  leaves a trail. A viewer that showed only successes would throw that away.

  The chain index is shown because the table is append only with a hash chain, and
  "jitctl audit verify" walks those indices. A gap visible on this screen is the signal that
  something was removed, which is the entire reason the chain exists.
*/

const SEVERITY_TONE: Readonly<Record<AuditSeverity, BadgeTone>> = {
  info: 'neutral',
  notice: 'info',
  warning: 'warning',
  critical: 'danger',
};

const OUTCOME_TONE: Readonly<Record<AuditOutcome, BadgeTone>> = {
  success: 'success',
  denied: 'warning',
  failure: 'danger',
};

export function AuditLogPage(): React.JSX.Element {
  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setTerm(input);
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [input]);

  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey: queryKeys.auditEvents(term),
    queryFn: () => listAuditEvents(term),
  });

  const events = data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Audit Log"
        description="Append-only record of every action, including the ones that were refused. Rows are hash chained, so a deletion is detectable rather than silent."
      />

      {isError ? (
        <Alert tone="danger" title="Could not load the audit log" liveness="assertive">
          {error instanceof Error ? error.message : 'Unknown error.'}
        </Alert>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
              aria-hidden="true"
            />
            <input
              type="search"
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
              }}
              aria-label="Search the audit log by action, actor, organization, target or detail"
              placeholder="Search actions, actors, targets"
              className="h-9 w-full rounded-[var(--radius-control)] border border-border bg-surface pr-3 pl-9 text-sm text-fg placeholder:text-fg-subtle hover:border-border-strong"
            />
          </div>
          <p aria-live="polite" className="text-xs text-fg-muted">
            {isPending ? (
              <Spinner label="Loading audit events" />
            ) : (
              <>
                <span className="font-semibold text-fg tabular-nums">
                  {formatNumber(events.length)}
                </span>{' '}
                {events.length === 1 ? 'event' : 'events'} shown
              </>
            )}
          </p>
          {isFetching && !isPending ? <Spinner label="Updating" /> : null}
        </div>

        {isPending ? (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="size-5" aria-hidden="true" />}
            title="No matching audit events"
            description="Broaden the search term. Retention removes rows on a documented schedule and records that it did, so an empty window is not necessarily an empty history."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[56rem] border-collapse text-left text-sm">
              <caption className="sr-only">
                Audit events, newest first. Each row shows the action, its outcome, who
                performed it, what it targeted, and its position in the hash chain.
              </caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  {['When', 'Action', 'Outcome', 'Actor', 'Target', 'Permission', 'Chain'].map(
                    (heading) => (
                      <th
                        key={heading}
                        scope="col"
                        className="px-3 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase"
                      >
                        {heading}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-b border-border last:border-b-0 align-top hover:bg-surface-hover">
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <time
                        dateTime={event.occurredAt}
                        title={formatAbsolute(event.occurredAt)}
                        className="text-xs text-fg-muted"
                      >
                        {formatRelative(event.occurredAt)}
                      </time>
                    </td>
                    <th scope="row" className="max-w-72 px-3 py-2.5 font-normal">
                      <span className="block text-sm font-medium text-fg">
                        {humaniseAction(event.action)}
                      </span>
                      <span className="block font-mono text-[11px] text-fg-subtle">
                        {event.action}
                      </span>
                      {event.detail !== null ? (
                        <span className="mt-1 block text-xs text-fg-muted">{event.detail}</span>
                      ) : null}
                    </th>
                    <td className="px-3 py-2.5">
                      <span className="flex flex-col items-start gap-1">
                        <Badge tone={OUTCOME_TONE[event.outcome]}>{event.outcome}</Badge>
                        <Badge tone={SEVERITY_TONE[event.severity]}>{event.severity}</Badge>
                      </span>
                    </td>
                    <td className="max-w-44 px-3 py-2.5">
                      <span className="block truncate text-fg">{event.actorName ?? 'System'}</span>
                      {event.actorIp !== null ? (
                        <span className="block font-mono text-[11px] text-fg-subtle">
                          {event.actorIp}
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-44 px-3 py-2.5">
                      <span className="block truncate text-fg-muted">
                        {event.targetLabel ?? 'None'}
                      </span>
                      <span className="block text-[11px] text-fg-subtle">
                        {event.targetType ?? ''}
                        {event.organizationName === null ? '' : ` · ${event.organizationName}`}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {event.permission === null ? (
                        <span className="text-xs text-fg-subtle">Not applicable</span>
                      ) : (
                        <span className="font-mono text-[11px] text-fg-muted">
                          {event.permission}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 font-mono text-[11px] text-fg-subtle tabular-nums">
                        <Link2 className="size-3" aria-hidden="true" />
                        {formatNumber(event.chainIndex)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
